<template>
    <div class="mobipass-main-container" v-if="!isLoading">
        <div class="title-wrapper">
            <p class="go-back" @click="goBack">
                <i class="fa-solid fa-arrow-left"></i>
                {{ $t("navigation.back") }}
            </p>
            <h1 class="mobipass-title">
                {{
                    $route.params.action === "create"
                        ? $t("mobipass.add")
                        : $t("mobipass.title")
                }}
            </h1>
        </div>
        <div class="mobipass-content-wrapper">
            <div class="centrale-infos-wrapper" v-if="displayLeftPart">
                <h4>{{ $t("mobipass.centraleInfos.title") }}</h4>
                <div class="field-wrapper">
                    <p>{{ $t("module.toReplace.centrale") }}</p>
                    <BasicInput
                        class="input"
                        :placeholder="centraleInfos.centraleName"
                        name="central-infos-centraleName"
                        disabled
                    />
                </div>
                <div class="field-wrapper">
                    <p>{{ $t("mobipass.centraleInfos.serial") }}</p>
                    <BasicInput
                        class="input"
                        :placeholder="centraleInfos.serial"
                        name="central-infos-serial"
                        disabled
                    />
                </div>
                <div class="field-wrapper">
                    <p>{{ $t("bloc_erp.tel") }}</p>
                    <BasicInput
                        class="input"
                        :placeholder="centraleInfos.telephone"
                        name="central-infos-telephone"
                        disabled
                    />
                </div>
                <BasicButton class="next-button" @click="activeRightPart">
                    {{ $t("button.next") }}
                </BasicButton>
            </div>
            <div class="separator"></div>
            <div class="mobipass-infos-wrapper" v-if="displayRightPart">
                <h4>{{ $t("mobipass.title") }}</h4>

                <MobipassCard class="mobipass-card" v-if="isCreatePage" />

                <div class="field-wrapper">
                    <div class="serial-number-wrapper">
                        <p>
                            {{ $t("mobipass.serialNumber") }}
                        </p>
                    </div>
                    <BasicInput
                        v-if="isCreatePage"
                        class="input"
                        :maxlength="11"
                        v-model="mobipassSerial"
                        name="mobipass-infos-serial"
                    >
                        <template #maxLengthCount>
                            {{ mobipassSerial?.replace(/-/g, "").length ?? 0 }}
                            /
                            {{ maxLengthMobipassSerial }}
                        </template>
                    </BasicInput>
                    <BasicInput
                        v-else
                        class="input"
                        :disabled="true"
                        :placeholder="mobipassSerial"
                        name="mobipass-infos-serial"
                    >
                        <template #maxLengthCount>
                            {{ mobipassSerial?.replace(/-/g, "").length ?? 0 }}
                            /
                            {{ maxLengthMobipassSerial }}
                        </template>
                    </BasicInput>
                </div>
                <div class="field-wrapper" v-if="isCreatePage">
                    <p>
                        {{ $t("mobipass.activationNumber") }}
                    </p>
                    <BasicInput
                        class="input"
                        v-model="mobipassInfos.activationNumber"
                        name="mobipass-infos-activation-number"
                    />
                </div>
                <div class="buttons-wrapper">
                    <BasicButton
                        v-if="isCreatePage"
                        @click="callAddMobipassOnCentralUnit()"
                        :disabled="registerButtonShouldBeDisabled"
                        :loading="isCreateOrUpdateLoading"
                    >
                        {{ $t("button.register") }}
                    </BasicButton>

                    <BasicButton
                        color="blue"
                        @click="goBack"
                        v-if="isCreatePage"
                    >
                        {{ $t("button.cancel") }}
                    </BasicButton>

                    <BasicButton
                        v-else
                        @click="callDeleteMobipass()"
                        :disabled="deleteButtonShouldBeDisabled"
                        :loading="isCreateOrUpdateLoading"
                    >
                        {{ $t("button.delete") }}
                    </BasicButton>
                </div>
            </div>
        </div>
    </div>
    <div class="loader-wrapper" v-else>
        <Loader />
    </div>
</template>

<script>
import {
    getCentraleById,
    addMobipassOnCentralUnit,
    getMobipassOnCentralUnit,
    deleteMobipassOnCentralUnit,
} from "@/services/intratone/centralUnit"

import BasicInput from "@/components/basic/BasicInput.vue"
import BasicButton from "@/components/basic/BasicButton.vue"
import Loader from "@/components/basic/Loader.vue"
import { useToast } from "vue-toastification"
import MobipassCard from "@/components/mobipass/MobipassCard.vue"

const toast = useToast()

export default {
    components: {
        BasicInput,
        BasicButton,
        Loader,
        MobipassCard,
    },

    data() {
        return {
            centraleInfos: {
                centraleName: null,
                residenceName: null,
                serial: null,
                telephone: null,
            },
            mobipassInfos: {
                serial: null,
                activationNumber: null,
                expired: false,
                activatedByDefault: false,
            },
            displayLeftPart: true,
            displayRightPart: window.innerWidth >= 1024,
            centralId: this.$route.params.centralId,
            maxLengthMobipassSerial: 9,
            isLoading: false,
            isCreateOrUpdateLoading: false,
        }
    },

    computed: {
        isCreatePage() {
            return this.$route.params.action === "create"
        },

        registerButtonShouldBeDisabled() {
            if (this.isCreateOrUpdateLoading) return true

            const hasSerial = !!this.mobipassInfos.serial

            const hasValidSerial = new RegExp(
                `^[A-Za-z0-9]{${this.maxLengthMobipassSerial}}$`,
            ).test(this.mobipassInfos.serial)

            const hasValidActivationNumber =
                this.mobipassInfos.activationNumber?.length > 0

            return !hasSerial || !hasValidSerial || !hasValidActivationNumber
        },

        deleteButtonShouldBeDisabled() {
            if (this.isCreateOrUpdateLoading) return true

            if (this.mobipassInfos.activatedByDefault) {
                return true
            }

            return false
        },

        mobipassSerial: {
            get() {
                return (
                    this.mobipassInfos.serial
                        ?.replace(/-/g, "")
                        .toUpperCase()
                        .match(/.{1,3}/g)
                        ?.join("-") ?? this.mobipassInfos.serial
                )
            },
            set(value) {
                const cleaned = value.replace(/-/g, "")
                this.mobipassInfos.serial = cleaned.slice(0, 9)
            },
        },
        maxLengthWithoutDashes() {
            return (
                this.maxLengthMobipassSerial +
                Math.floor((this.maxLengthMobipassSerial - 1) / 3)
            )
        },
    },

    methods: {
        async callGetCentraleInfos() {
            this.isLoading = true

            await getCentraleById(this.centralId)
                .then((response) => {
                    this.centraleInfos.centraleName = response.data.nom
                    this.centraleInfos.serial = response.data.serie
                    this.centraleInfos.telephone = response.data.tel

                    this.mobipassInfos.serial = response.data.mobipass
                    this.mobipassInfos.activatedByDefault =
                        response.data.mobipass_default === 1
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        callAddMobipassOnCentralUnit() {
            this.isCreateOrUpdateLoading = true

            addMobipassOnCentralUnit(this.centralId, {
                serial: this.mobipassInfos.serial,
                code: this.mobipassInfos.activationNumber,
            })
                .then((response) => {
                    if (response.error !== 1) {
                        this.$router.replace({ params: { action: "edit" } })
                        toast.success(
                            this.$t("mobipass.confirmCreationSuccess"),
                        )
                    }
                })
                .catch((error) => {
                    console.log(error)
                })
                .finally(() => {
                    this.isCreateOrUpdateLoading = false
                })
        },

        callDeleteMobipass() {
            this.isCreateOrUpdateLoading = true

            getMobipassOnCentralUnit(this.centralId)
                .then((response) => {
                    if (response.error !== 1) {
                        this.openModal("validation", {
                            title: "deleteMobipass",
                            question: this.$t("mobipass.confirmToDelete"),
                            validateButton: this.$t("button.delete"),
                            valid: () =>
                                deleteMobipassOnCentralUnit(
                                    this.centralId,
                                ).then((response) => {
                                    if (response.error !== 1) {
                                        toast.success(
                                            this.$t(
                                                "mobipass.confirmDeletionSuccess",
                                            ),
                                        )
                                        this.goBack()
                                    }
                                }),
                        })
                    }
                })
                .finally(() => {
                    this.isCreateOrUpdateLoading = false
                })
        },

        activeRightPart() {
            this.displayLeftPart = false
            this.displayRightPart = true
        },

        goBack() {
            this.$router.go(-1)
        },
    },

    mounted() {
        window.addEventListener("resize", (event) => {
            if (event.target.innerWidth >= 1024) {
                this.displayRightPart = true
            } else {
                this.displayRightPart = false
            }
        })
        this.callGetCentraleInfos()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global.scss";

h1,
h4,
p {
    margin: 0;
}

h4 {
    font-weight: 800;
}

.mobipass-main-container {
    width: 100%;
    // max-width: 1920px;
    padding: 20px 80px;
    display: flex;
    flex-direction: column;
    gap: 2em;

    @media all and (max-width: 1024px) {
        min-width: 0;
        padding: 20px;
    }

    .go-back {
        color: $orange-neutral;
        width: fit-content;

        &:hover {
            cursor: pointer;
        }

        i {
            margin-right: 5px;
        }
    }

    .title-wrapper {
        display: flex;
        flex-direction: column;
        gap: 0.2em;

        .mobipass-title {
            font-family: $font_avenir_black;
        }
    }

    .mobipass-content-wrapper {
        display: flex;
        width: 100%;
        height: 100%;
        gap: 4em;

        .separator {
            width: 2px;
            background-color: $blue-neutral;

            @media all and (max-width: 1024px) {
                display: none;
            }
        }

        .centrale-infos-wrapper,
        .mobipass-infos-wrapper {
            width: 50%;
            max-width: 700px;
            display: flex;
            flex-direction: column;
            gap: 1em;

            .serial-number-wrapper p {
                max-width: 140px;
                text-wrap: wrap;
            }

            .mobipass-card {
                @media all and (max-width: 768px) {
                    display: none;
                }
            }

            @media all and (max-width: 1024px) {
                width: 100%;
                max-width: 500px;
            }

            .field-wrapper {
                display: flex;
                align-items: center;
                gap: 1.25rem;
                width: 100%;

                @media all and (max-width: 768px) {
                    flex-direction: column;
                    align-items: start;
                    gap: 0.25rem;
                }

                p {
                    min-width: 165px;
                    width: max-content;

                    @media all and (max-width: 768px) {
                        max-width: 100%;
                    }
                }

                .input {
                    flex: 1;
                    width: 100%;
                    max-width: 100%;
                }
            }

            .next-button {
                max-width: max-content;

                @media all and (min-width: 1024px) {
                    display: none;
                }
            }

            .buttons-wrapper {
                display: flex;
                align-items: center;
                gap: 0.5em;
            }
        }
    }
}

.loader-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
